import {
  List,
  Datagrid,
  FunctionField,
  ListProps,
  useListContext,
  TextInput,
  SelectInput,
  DateInput,
} from 'react-admin';
import { Box, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';
import React, { useState } from 'react';
import AuditLogDetailDialog from './AuditLogDetailDialog.tsx';
import { formatNZTime } from '../../../utils/dateUtils.ts';

const OPERATION_CHOICES = [
  'Login', 'LoginFailed', 'Logout',
  'Create', 'Update', 'Delete', 'View',
  'Export', 'Import', 'Upload', 'Download',
  'Approve', 'Reject', 'Submit', 'Recall',
  'PermissionChange', 'RoleChange', 'Configuration',
].map((op) => ({ id: op, name: op }));

const RESULT_CHOICES = [
  { id: 'Success', name: 'Success' },
  { id: 'Fail', name: 'Fail' },
];

const auditLogFilters = [
  <TextInput source="keyword" label="Search" alwaysOn />,
  <SelectInput source="operation" label="Operation" choices={OPERATION_CHOICES} />,
  <SelectInput source="result" label="Result" choices={RESULT_CHOICES} />,
  <TextInput source="module" label="Module" />,
  <TextInput source="userName" label="User" />,
  <DateInput source="dateFrom" label="From" />,
  <DateInput source="dateTo" label="To" />,
];

const AuditLogListHeader = () => {
  const { total } = useListContext();

  return (
    <Paper elevation={0} sx={{ mb: 2, p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#0f172a', mb: 0.5 }}>Audit Log</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>System-wide record of security, data, approval, and configuration events.</Typography>
        </Box>
        <Chip label={`Total: ${total ?? 0}`} sx={{ height: 32, fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }} />
      </Stack>
    </Paper>
  );
};

const EllipsisCell = ({ value }: { value?: React.ReactNode }) => (
  <Tooltip title={value || '-'} arrow placement="top" enterDelay={400}>
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
      }}
    >
      {value || '-'}
    </Box>
  </Tooltip>
);

const RESULT_COLORS: Record<string, { bgcolor: string; color: string }> = {
  Success: { bgcolor: '#dcfce7', color: '#166534' },
  Fail: { bgcolor: '#fee2e2', color: '#991b1b' },
};

const SEVERITY_COLORS: Record<string, { bgcolor: string; color: string }> = {
  Info: { bgcolor: '#f1f5f9', color: '#334155' },
  Warning: { bgcolor: '#fef3c7', color: '#92400e' },
  Error: { bgcolor: '#fee2e2', color: '#991b1b' },
};

const ViewButton = ({ record }: { record?: any }) => {
  const [open, setOpen] = useState(false);
  if (!record) return null;

  return (
    <>
      <Tooltip title="View details" arrow placement="top">
        <IconButton size="small" onClick={() => setOpen(true)}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {open && <AuditLogDetailDialog id={record.id} open={open} onClose={() => setOpen(false)} />}
    </>
  );
};

const AuditLogList = (props: ListProps) => {
  return (
    <List
      sx={{ '& .RaList-content': { p: '0 !important' } }}
      empty={false}
      filters={auditLogFilters}
      sort={{ field: 'logTime', order: 'DESC' }}
      perPage={25}
      {...props}
    >
      <AuditLogListHeader />
      <Paper elevation={0} sx={{ p: 0.75, borderRadius: '14px', overflow: 'visible', border: '1px solid #e5e7eb', boxShadow: '0 6px 18px rgba(15, 23, 42, 0.05)', bgcolor: '#fff' }}>
        <Datagrid
          rowClick={false}
          bulkActionButtons={false}
          sx={{
            borderRadius: '10px',
            overflow: 'hidden',
            tableLayout: 'fixed',
            width: '100%',
            '& .RaDatagrid-table': { tableLayout: 'fixed', width: '100%' },
            '& .RaDatagrid-headerCell': { textAlign: 'center', bgcolor: '#f8fafc', fontWeight: 700, color: '#334155', py: 1.25 },
            '& .RaDatagrid-rowCell': { textAlign: 'center', py: 1.1, verticalAlign: 'middle', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
            '& .RaDatagrid-headerCell:first-of-type, & .RaDatagrid-rowCell:first-of-type': { pl: 2 },
            '& .RaDatagrid-headerCell:last-of-type, & .RaDatagrid-rowCell:last-of-type': { pr: 1.5 },
            '& .RaDatagrid-row:nth-of-type(even)': { bgcolor: '#fbfdff' },
            '& .RaDatagrid-row:hover': { bgcolor: '#f8fafc' },
          }}
        >
          <FunctionField
            label="Time"
            render={(record: any) => <EllipsisCell value={formatNZTime(record.logTime)} />}
            sx={{ width: '18%' }}
          />
          <FunctionField
            label="Operation"
            render={(record: any) => {
              const colors = SEVERITY_COLORS[record.severity] || SEVERITY_COLORS.Info;
              return <Chip size="small" label={record.operation} sx={{ fontWeight: 700, ...colors }} />;
            }}
            sx={{ width: '10%' }}
          />
          <FunctionField label="Module" render={(record: any) => <EllipsisCell value={record.module} />} sx={{ width: '10%' }} />
          {/* <FunctionField
            label="Entity"
            render={(record: any) => <EllipsisCell value={record.entityName ? `${record.entityName} #${record.entityId ?? '-'}` : '-'} />}
            sx={{ width: '12%' }}
          /> */}
          <FunctionField label="User" render={(record: any) => <EllipsisCell value={record.userName} />} sx={{ width: '10%' }} />
          <FunctionField label="IP Address" render={(record: any) => <EllipsisCell value={record.ipAddress} />} sx={{ width: '9%' }} />
          <FunctionField
            label="Result"
            render={(record: any) => {
              const colors = RESULT_COLORS[record.result] || RESULT_COLORS.Success;
              return <Chip size="small" label={record.result} sx={{ fontWeight: 700, ...colors }} />;
            }}
            sx={{ width: '8%' }}
          />
          <FunctionField
            label="Description"
            render={(record: any) => <EllipsisCell value={record.description} />}
            sx={{ width: '20%' }}
          />
          <FunctionField
            label="Details"
            render={(record: any) => <ViewButton record={record} />}
            sx={{ width: '8%' }}
          />
        </Datagrid>
      </Paper>
    </List>
  );
};

export default AuditLogList;
